import Swiper from 'swiper';
import { Navigation, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

export function initCompaniesTabs() {
    const tabsContainer = document.querySelector('[data-companies-tabs]');
    
    if (!tabsContainer) return;
    
    const tabButtons = tabsContainer.querySelectorAll('[data-tab-button]');
    const tabPanels = tabsContainer.querySelectorAll('[data-tab-panel]');
    const sliderElement = tabsContainer.querySelector('.companies-tabs-slider');
    
    if (tabPanels.length === 0) return;
    
    // Initialize Swiper for mobile sliding
    let tabsSwiper = null;
    if (sliderElement) {
        tabsSwiper = new Swiper(sliderElement, {
            slidesPerView: 'auto',
            spaceBetween: 20,
            freeMode: true,
            breakpoints: {
                0: { spaceBetween: 20 },
                768: { spaceBetween: 36 }
            }
        });
    }
    
    // Constants
    const SIDEBAR_WIDTH = 420;
    const ANIMATION_DURATION = 300;
    const LG_BREAKPOINT = 1024;
    
    // Cache for panel elements to avoid repeated DOM queries
    const panelCache = new Map();
    const companyDetailSwipers = new Map();
    
    // Helper: Check if mobile layout
    const isMobileLayout = () => window.innerWidth < LG_BREAKPOINT;
    
    // Helper: Toggle button image state
    function setButtonImageState(btn, isActive) {
        const hoverImg = btn.querySelector('img.group-hover\\:block');
        const normalImg = btn.querySelector('img.group-hover\\:hidden');
        if (!hoverImg || !normalImg) return;
        
        btn.classList.toggle('logo-active', isActive);
        hoverImg.classList.toggle('hidden', !isActive);
        hoverImg.classList.toggle('block', isActive);
        normalImg.classList.toggle('hidden', isActive);
    }
    
    // Helper: Reset all logo buttons in a panel
    function resetLogoButtons(logoButtons) {
        logoButtons.forEach(btn => setButtonImageState(btn, false));
    }
    
    // Helper: Get cached panel elements
    function getPanelElements(panel) {
        if (!panelCache.has(panel)) {
            panelCache.set(panel, {
                detailSlider: panel.querySelector('.company-details-slider'),
                detailSidebar: panel.querySelector('[data-company-detail-sidebar]'),
                closeButton: panel.querySelector('[data-close-company-detail]'),
                logoButtons: panel.querySelectorAll('[data-open-company-detail]'),
                prevButton: panel.querySelector('.company-details-prev'),
                nextButton: panel.querySelector('.company-details-next')
            });
        }
        return panelCache.get(panel);
    }
    
    // Helper: Check if sidebar is open
    function isSidebarOpen(detailSidebar) {
        if (!detailSidebar) return false;
        return isMobileLayout() 
            ? !detailSidebar.classList.contains('sidebar-closed')
            : detailSidebar.style.opacity === '1';
    }
    
    // Helper: Get target height for sidebar
    function getTargetHeight(detailSidebar) {
        const currentStyles = {
            width: detailSidebar.style.width,
            height: detailSidebar.style.height,
            maxHeight: detailSidebar.style.maxHeight,
            transform: detailSidebar.style.transform
        };
        
        detailSidebar.style.width = isMobileLayout() ? '100%' : `${SIDEBAR_WIDTH}px`;
        detailSidebar.style.height = 'auto';
        detailSidebar.style.maxHeight = 'none';
        detailSidebar.style.transform = 'none';
        
        const height = detailSidebar.scrollHeight;
        
        Object.assign(detailSidebar.style, currentStyles);
        return height;
    }
    
    // Close sidebar for a given panel
    function closeSidebar(panel) {
        const { detailSidebar, logoButtons } = getPanelElements(panel);
        if (!detailSidebar || !isSidebarOpen(detailSidebar)) return;
        
        if (isMobileLayout()) {
            detailSidebar.style.maxHeight = '0';
            detailSidebar.style.opacity = '0';
            detailSidebar.style.transform = 'translateY(-20px)';
            
            setTimeout(() => {
                panel.classList.remove('sidebar-active');
                detailSidebar.classList.add('sidebar-closed');
                resetLogoButtons(logoButtons);
            }, ANIMATION_DURATION);
        } else {
            const currentHeight = detailSidebar.offsetHeight;
            detailSidebar.style.height = `${currentHeight}px`;
            detailSidebar.offsetHeight; // Force reflow
            
            detailSidebar.style.opacity = '0';
            detailSidebar.style.width = '0';
            detailSidebar.style.height = '0';
            panel.classList.remove('sidebar-active');
            
            setTimeout(() => {
                detailSidebar.style.transform = 'translateX(100%)';
                resetLogoButtons(logoButtons);
            }, ANIMATION_DURATION);
        }
    }
    
    // Initialize sidebar state for a panel
    function initializeSidebar(detailSidebar) {
        if (isMobileLayout()) {
            detailSidebar.classList.add('sidebar-closed');
            Object.assign(detailSidebar.style, {
                maxHeight: '0',
                opacity: '0',
                transform: 'translateY(-20px)',
                width: '100%',
                height: 'auto'
            });
        } else {
            Object.assign(detailSidebar.style, {
                width: '0',
                height: '0',
                opacity: '0'
            });
        }
    }
    
    // Update active logo button style
    function updateActiveLogoButton(logoButtons, activeIndex) {
        logoButtons.forEach((btn, index) => setButtonImageState(btn, index === activeIndex));
    }
    
    // Get or create swiper for a panel
    function getOrCreateSwiper(panel) {
        if (!companyDetailSwipers.has(panel)) {
            const { detailSlider, prevButton, nextButton, logoButtons } = getPanelElements(panel);
            
            const detailSwiper = new Swiper(detailSlider, {
                modules: [Navigation, EffectFade],
                effect: 'fade',
                fadeEffect: { crossFade: true },
                slidesPerView: 1,
                spaceBetween: 0,
                speed: 300,
                loop: true,
                navigation: {
                    nextEl: nextButton,
                    prevEl: prevButton,
                },
                on: {
                    slideChange() {
                        updateActiveLogoButton(logoButtons, this.realIndex);
                    }
                }
            });
            companyDetailSwipers.set(panel, detailSwiper);
        }
        return companyDetailSwipers.get(panel);
    }
    
    // Scroll to sidebar on mobile
    function scrollToSidebar(detailSidebar) {
        if (!isMobileLayout()) return;
        
        setTimeout(() => {
            const sidebarRect = detailSidebar.getBoundingClientRect();
            const headerHeight = 80;
            const offset = 20;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            window.scrollTo({
                top: scrollTop + sidebarRect.top - headerHeight - offset,
                behavior: 'smooth'
            });
        }, 350);
    }
    
    // Open sidebar for a company
    function openSidebar(panel, companyIndex) {
        const { detailSidebar, logoButtons } = getPanelElements(panel);
        const detailSwiper = getOrCreateSwiper(panel);
        
        if (isSidebarOpen(detailSidebar)) {
            detailSwiper.slideTo(companyIndex, 300);
            updateActiveLogoButton(logoButtons, companyIndex);
            return;
        }
        
        panel.classList.add('sidebar-active');
        const targetHeight = getTargetHeight(detailSidebar);
        
        if (isMobileLayout()) {
            detailSidebar.classList.remove('sidebar-closed');
            detailSidebar.style.width = '100%';
            detailSidebar.style.height = 'auto';
            detailSidebar.offsetHeight; // Force reflow
            
            requestAnimationFrame(() => {
                detailSidebar.style.maxHeight = `${targetHeight}px`;
                detailSidebar.style.opacity = '1';
                detailSidebar.style.transform = 'translateY(0)';
                
                setTimeout(() => {
                    detailSwiper.update();
                    detailSwiper.slideTo(companyIndex, 0);
                    updateActiveLogoButton(logoButtons, companyIndex);
                }, 100);
                
                scrollToSidebar(detailSidebar);
            });
        } else {
            Object.assign(detailSidebar.style, {
                opacity: '0',
                maxHeight: '',
                width: `${SIDEBAR_WIDTH}px`,
                height: `${targetHeight}px`,
                transform: 'translateX(100%)'
            });
            detailSidebar.classList.remove('sidebar-closed');
            
            requestAnimationFrame(() => {
                detailSwiper.update();
                detailSwiper.slideTo(companyIndex, 0);
                updateActiveLogoButton(logoButtons, companyIndex);
                
                requestAnimationFrame(() => {
                    detailSidebar.style.transform = 'translateX(0)';
                    setTimeout(() => {
                        detailSidebar.style.opacity = '1';
                    }, ANIMATION_DURATION);
                });
            });
        }
    }
    
    // Handle resize for all panels
    function handleResize(detailSidebar, panel) {
        const wasOpen = isSidebarOpen(detailSidebar);
        
        if (wasOpen) {
            const targetHeight = getTargetHeight(detailSidebar);
            if (isMobileLayout()) {
                detailSidebar.classList.remove('sidebar-closed');
                Object.assign(detailSidebar.style, {
                    maxHeight: `${targetHeight}px`,
                    width: '100%',
                    height: 'auto',
                    opacity: '1',
                    transform: 'translateY(0)'
                });
            } else {
                detailSidebar.classList.remove('sidebar-closed');
                Object.assign(detailSidebar.style, {
                    width: `${SIDEBAR_WIDTH}px`,
                    height: `${targetHeight}px`,
                    opacity: '1',
                    maxHeight: '',
                    transform: ''
                });
            }
        } else {
            initializeSidebar(detailSidebar);
        }
    }
    
    // Setup each panel
    tabPanels.forEach(panel => {
        const elements = getPanelElements(panel);
        const { detailSlider, detailSidebar, closeButton } = elements;
        
        if (!detailSlider || !detailSidebar) return;
        
        initializeSidebar(detailSidebar);
        
        if (closeButton) {
            closeButton.addEventListener('click', () => closeSidebar(panel));
        }
    });
    
    // Event delegation for logo button clicks
    tabsContainer.addEventListener('click', (e) => {
        const logoButton = e.target.closest('[data-open-company-detail]');
        if (!logoButton) return;
        
        const panel = logoButton.closest('[data-tab-panel]');
        if (!panel) return;
        
        const companyIndex = parseInt(logoButton.dataset.companyIndex, 10);
        setButtonImageState(logoButton, true);
        openSidebar(panel, companyIndex);
    });
    
    // Single resize listener for all panels
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            tabPanels.forEach(panel => {
                const { detailSidebar } = getPanelElements(panel);
                if (detailSidebar) {
                    handleResize(detailSidebar, panel);
                }
            });
        }, 150);
    });
    
    // Click outside to close sidebar
    document.addEventListener('click', (e) => {
        const activePanel = Array.from(tabPanels).find(p => p.classList.contains('tab-panel-active'));
        if (!activePanel) return;
        
        const { detailSidebar, logoButtons } = getPanelElements(activePanel);
        if (!isSidebarOpen(detailSidebar)) return;
        
        const isClickInsideSidebar = detailSidebar.contains(e.target);
        const isClickOnLogoButton = Array.from(logoButtons).some(btn => btn.contains(e.target));
        
        if (!isClickInsideSidebar && !isClickOnLogoButton) {
            closeSidebar(activePanel);
        }
    });
    
    // Tab switching
    function switchTab(tabId, slideIndex) {
        const currentPanel = Array.from(tabPanels).find(p => p.classList.contains('tab-panel-active'));
        const targetPanel = Array.from(tabPanels).find(p => p.dataset.tabPanel === tabId);
        
        if (!targetPanel || currentPanel === targetPanel) return;
        
        if (currentPanel) {
            closeSidebar(currentPanel);
            currentPanel.classList.remove('tab-panel-active');
            currentPanel.setAttribute('aria-hidden', 'true');
        }
        
        tabButtons.forEach(button => {
            const isActive = button.dataset.tabButton === tabId;
            button.classList.toggle('font-bold', isActive);
            button.classList.toggle('text-red', isActive);
            button.classList.toggle('border-b-1', isActive);
            button.classList.toggle('border-red', isActive);
            button.classList.toggle('text-black/64', !isActive);
            button.setAttribute('aria-selected', isActive);
        });
        
        if (tabsSwiper && slideIndex !== undefined) {
            tabsSwiper.slideTo(slideIndex, 300);
        }
        
        targetPanel.classList.add('tab-panel-active');
        targetPanel.setAttribute('aria-hidden', 'false');
    }
    
    // Tab button click handlers
    tabButtons.forEach((button, index) => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            switchTab(button.dataset.tabButton, index);
        });
    });
    
    // Initialize first tab
    if (tabButtons.length > 0) {
        const firstTabId = tabButtons[0].dataset.tabButton;
        tabButtons[0].classList.add('font-bold', 'text-[#CD3838]', 'border-b-1', 'border-[#CD3838]');
        tabButtons[0].classList.remove('text-black/64');
        
        const firstPanel = Array.from(tabPanels).find(p => p.dataset.tabPanel === firstTabId);
        if (firstPanel) {
            firstPanel.classList.add('tab-panel-active');
        }
    } else if (tabPanels.length > 0) {
        tabPanels[0].classList.add('tab-panel-active');
        tabPanels[0].setAttribute('aria-hidden', 'false');
    }
}
